import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDomesticV1CartDeleteMutation } from '~/app/react-query/domestic/cart/v1'
import type { DomesticCartConverterResponse } from '~/api/routes/domestic/cart/controller/type'
import { showToast } from '@yanolja-rnd/nol-design-system'
import { DOMESTIC_CART_QUERY_KEY } from '~/app/react-query/domestic/cart/constants'
import { PostCartDeleteDto } from '~/api/routes/domestic/cart/controller/dto'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'
import { getYaNative } from '@yanolja-org/ya-native'
import { isNative } from '~/app/utils/typeguards'

/**
 * 장바구니 옵션 삭제를 위한 공통 hook
 * Optimistic update와 롤백 기능을 포함한 안전한 삭제 기능을 제공합니다.
 */
export const useDomesticCartDelete = () => {
  const { unselectOptions, selectAllOptions } = useDomesticCartStore()
  const queryClient = useQueryClient()
  const { mutateAsync: deleteDomesticCartMutate } = useDomesticV1CartDeleteMutation({
    onMutate: async (variables: PostCartDeleteDto['body']) => {
      // 1. 진행 중인 domesticCartQuery 리패치를 취소
      await queryClient.cancelQueries({ queryKey: [DOMESTIC_CART_QUERY_KEY] })

      // 2. 현재 데이터를 백업으로 저장
      const previousCartData = queryClient.getQueryData<DomesticCartConverterResponse>([
        DOMESTIC_CART_QUERY_KEY,
      ])

      // 3. Optimistic update 실행
      queryClient.setQueryData<DomesticCartConverterResponse>(
        [DOMESTIC_CART_QUERY_KEY],
        oldData => {
          if (!oldData) return oldData

          return {
            ...oldData,
            productSection: {
              ...oldData.productSection,
              cartItems: oldData.productSection.cartItems
                .map(item => {
                  const filteredOptions = item.options.filter(
                    option => !variables.optionIds.includes(option.optionId)
                  )
                  return {
                    ...item,
                    options: filteredOptions,
                  } as typeof item
                })
                .filter(item => item.options.length > 0),
            },
          }
        }
      )

      return previousCartData
    },
    onSuccess: () => {
      // app 인 경우 상품 삭제가 되었을 때, 장바구니 뱃지의 카운트 노출 sync 요청
      const yaNative = getYaNative()
      if (isNative(yaNative)) {
        yaNative.requestSync({ at: 'cart' })
      }
    },
    onError: (_error, _variables, previousData) => {
      if (previousData) {
        // 저장된 이전 데이터로 복구
        queryClient.setQueryData<DomesticCartConverterResponse>(
          [DOMESTIC_CART_QUERY_KEY],
          previousData
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [DOMESTIC_CART_QUERY_KEY] })
    },
  })

  /**
   * 장바구니 옵션들을 삭제하는 함수
   * @param optionIdsToRemove 삭제할 옵션 ID 배열
   * @param onSuccess 삭제 성공 시 콜백
   * @param onError 삭제 실패 시 콜백
   * @param onSettled 삭제 완료 시 콜백 (성공/실패 관계없이)
   */
  const deleteCartOptions = useCallback(
    async (
      optionIdsToRemove: string[],
      callbacks?: {
        onSuccess?: () => void
        onError?: () => void
        onSettled?: () => void
      }
    ) => {
      // Optimistic update: 옵션들을 즉시 store에서 제거
      unselectOptions(optionIdsToRemove)
      await deleteDomesticCartMutate(
        { optionIds: optionIdsToRemove },
        {
          onSuccess: () => {
            callbacks?.onSuccess?.()
          },
          onError: () => {
            // 에러 시: store 롤백
            selectAllOptions(optionIdsToRemove)
            showToast({
              children: '상품을 삭제하지 못했어요',
            })
            callbacks?.onError?.()
          },
          onSettled: () => {
            callbacks?.onSettled?.()
          },
        }
      )
    },
    [deleteDomesticCartMutate, unselectOptions, selectAllOptions]
  )

  return {
    deleteCartOptions,
  }
}

export default useDomesticCartDelete
